import { memo, useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Alert, Box, Typography } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { FormContainer } from 'components/layout';
import { ChipInput, GroupedSelectableGrid } from 'components/common';
import datadogActions from 'lib/datadog';
import { useLocationParams } from 'hooks';
import useTwitterLineItemsData from '../hooks/useTwitterLineItemsData';
import type { TwitterGridRow } from '../types';
import { getTwitterGridRowId, isTwitterLineItemRow } from '../types';
import { toIdSelectionEntries } from '../utils/idSelection';
import { formatEntityStatus } from '../utils/entityStatus';

export const UNPARSEABLE_FLOW_TOOLTIP = 'Flow is missing or the ad group name is incorrectly structured';
export const NO_SELECTABLE_LINE_ITEMS_HINT = 'No ad groups with a defined flow';

const cellText = (value: string, hint?: string) => (
  <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', gap: 1 }}>
    <Typography variant="body2">{value}</Typography>
    {hint ? (
      <Typography variant="caption" color="text.secondary">
        — {hint}
      </Typography>
    ) : null}
  </Box>
);

const hasSelectableLineItems = (row: TwitterGridRow) =>
  isTwitterLineItemRow(row) || row.line_items.some((lineItem) => !!lineItem.flow);

const COLUMNS: GridColDef<TwitterGridRow>[] = [
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    minWidth: 400,
    sortable: false,
    renderCell: (params: GridRenderCellParams<TwitterGridRow>) =>
      isTwitterLineItemRow(params.row)
        ? cellText(params.row.line_item_name)
        : cellText(
            params.row.campaign_name,
            hasSelectableLineItems(params.row) ? undefined : NO_SELECTABLE_LINE_ITEMS_HINT,
          ),
  },
  {
    field: 'flow',
    headerName: 'Flow',
    width: 240,
    sortable: false,
    renderCell: (params: GridRenderCellParams<TwitterGridRow>) =>
      isTwitterLineItemRow(params.row) ? cellText(params.row.flow ?? '—') : null,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 110,
    sortable: false,
    renderCell: (params: GridRenderCellParams<TwitterGridRow>) =>
      isTwitterLineItemRow(params.row) ? cellText(formatEntityStatus(params.row.status)) : null,
  },
];

// Ads need the flow for texts and URL, so a line item without a parsed flow cannot be picked.
const isSelectableRow = (row: TwitterGridRow) => isTwitterLineItemRow(row) && !!row.flow;
const getRowTooltip = (row: TwitterGridRow) =>
  isTwitterLineItemRow(row) && !row.flow ? UNPARSEABLE_FLOW_TOOLTIP : undefined;

const ChooseLineItems = memo(() => {
  const {
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();
  const lineItemsError = errors.selectedLineItems?.message as string | undefined;

  const { gridRows, selectedLineItems, isLoading, isError, errorMessage } = useTwitterLineItemsData();
  const filterByName = useWatch({ name: 'filterLineItemsByName' }) as string[] | undefined;
  const { setSelectedAdSets: setSelectedAdSetsParam, setFilterAdSetsByName: setFilterAdSetsByNameParam } =
    useLocationParams();

  useEffect(() => {
    setSelectedAdSetsParam(toIdSelectionEntries(selectedLineItems));
  }, [selectedLineItems, setSelectedAdSetsParam]);

  useEffect(() => {
    setFilterAdSetsByNameParam(filterByName ?? []);
  }, [filterByName, setFilterAdSetsByNameParam]);

  const handleSelectionChange = useCallback(
    (ids: string[]) => {
      setValue('selectedLineItems', ids, { shouldValidate: true, shouldDirty: true });
    },
    [setValue],
  );

  useEffect(() => {
    void trigger('selectedLineItems');
  }, [trigger]);

  useEffect(() => {
    if (!isError) return;

    datadogActions.trackDatadogError(new Error(errorMessage ?? 'Failed to load ad groups'), {
      source: 'twitter_choose_line_items_query',
    });
  }, [isError, errorMessage]);

  return (
    <FormContainer
      title="Choose Ad Groups"
      error={lineItemsError}
      sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2, height: '100%' }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%' }}>
        <Box sx={{ flex: 1, maxWidth: '50%' }}>
          <ChipInput name="filterLineItemsByName" inputLabel="Enter ad group name" />
        </Box>
      </Box>

      {isError ? (
        <Alert severity="error" sx={{ width: '100%' }} data-testid="twitter-line-items-error">
          {errorMessage}
        </Alert>
      ) : (
        <GroupedSelectableGrid
          rows={gridRows}
          columns={COLUMNS}
          isChildRow={isTwitterLineItemRow}
          getRowId={getTwitterGridRowId}
          selectedIds={selectedLineItems}
          onSelectionChange={handleSelectionChange}
          isRowSelectable={isSelectableRow}
          getRowTooltip={getRowTooltip}
          isLoading={isLoading}
          footerMessage={lineItemsError}
        />
      )}
    </FormContainer>
  );
});

ChooseLineItems.displayName = 'ChooseLineItems';

export default ChooseLineItems;
